/**
 * Essential Image Utilities for PDF Generation
 * 
 * Only the utilities actually needed for @react-pdf/renderer that don't exist elsewhere.
 */

/**
 * Convert image URL to base64 string
 * 
 * NOTE: This requires S3 bucket to have CORS configured to allow browser access.
 * If you get CORS errors, the backend team needs to add this to S3 bucket CORS config:
 * 
 * [
 *   {
 *     "AllowedHeaders": ["*"],
 *     "AllowedMethods": ["GET"],
 *     "AllowedOrigins": ["http://localhost:3000", "https://yourdomain.com"],
 *     "MaxAgeSeconds": 3000
 *   }
 * ]
 * 
 * @param imageUrl - The image URL to convert (e.g., S3 presigned URL)
 * @returns Promise<string> - Base64 encoded image data
 */
export const convertBlobToBase64 = async (imageUrl: string): Promise<string> => {
  try {
    // Fetch the image (S3 presigned URLs don't need auth headers)
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    
    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(new Error('Failed to read image blob'));
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    
    // Check if it's a CORS error
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('CORS ERROR: S3 bucket needs CORS configuration. Contact backend team to add CORS policy to allow browser access.');
    }
    
    throw new Error(`Failed to load image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Common image sizes for PDF documents
 */
export const PDF_IMAGE_SIZES = {
  LOGO: { maxWidth: 200, maxHeight: 100 },
  SIGNATURE: { maxWidth: 150, maxHeight: 75 },
} as const;
